import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { BUSINESS_FACTS, PRICING_TABLE, EXTRAS_TABLE, FAQ_CATEGORIES, ALL_FAQS, CITY_PAGE_AREAS } from '../data/knowledgeBase';
import { businessEntity, websiteEntity, injectJsonLd, setHeadMeta, canonicalUrl, BUSINESS_ID, WEBSITE_ID, SITE_URL } from '../utils/seo';
const PATH = '/faq';
const TITLE = "Mobile Car Detailing FAQ & Pricing in Ventura County | Mike's Pro Detail";
const DESCRIPTION = "Answers to common questions about mobile car detailing from Mike's Pro Detail — pricing, what's included, service areas, scheduling, and payment. Call (818) 381-9905.";
/**
 * /faq — the customer information page.
 *
 * A single, plain-text, crawlable page carrying the official answer to every
 * common customer question, plus the pricing table and business facts. Built
 * entirely from the shared knowledge base so nothing here can contradict the
 * rest of the site. Designed to match the existing site exactly.
 */
export function FaqPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setHeadMeta({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      ogType: 'article'
    });
    const url = canonicalUrl(PATH);
    return injectJsonLd([businessEntity, websiteEntity, {
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      url,
      name: 'Mobile Car Detailing FAQ',
      description: DESCRIPTION,
      isPartOf: {
        '@id': WEBSITE_ID
      },
      about: {
        '@id': BUSINESS_ID
      },
      mainEntity: ALL_FAQS.map(f => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer
        }
      }))
    }, {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'FAQ',
        item: url
      }]
    }]);
  }, []);
  return <main className="w-full bg-black text-white" data-id="element-755" data-mp="1xugyz5o1poh8">

      {/* Header */}
      <header className="w-full bg-black pt-36 sm:pt-40 pb-16 px-4 border-b border-zinc-900" data-id="element-756" data-mp="1xto00qvbw39n">
        <div className="max-w-4xl mx-auto" data-id="element-757" data-mp="1jug01vrccr4e">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-758" data-mp="1otixau12xt1t">
            <ol className="flex items-center gap-2 text-sm text-gray-500" data-id="element-759" data-mp="1ory1z26xy62w">
              <li data-id="element-760" data-mp="1a436je1jtnm4">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-761" data-mp="1a3ldeqa6npif">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" className="text-zinc-600" data-id="element-762" data-mp="114gbrb3brg21">
                /
              </li>
              <li className="text-gray-300" aria-current="page" data-id="element-763" data-mp="162ropuox26oh">
                FAQ
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl sm:text-4xl md:text-6xl font-black uppercase mb-4" data-id="element-764" data-mp="r7bot91nngk6">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-765" data-mp="w4261zp74i76">
              Mobile Detailing FAQ &amp; Customer Information
            </span>
          </h1>
          <div className="h-1 w-24 bg-red-600 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-766" data-mp="8jb0v509mo76" />
          <p className="text-gray-400 text-lg mt-6 max-w-3xl leading-relaxed" data-id="element-767" data-mp="mj2irgtwkoqg">
            Everything you need to know about mobile car detailing with Mike's
            Pro Detail — pricing, what each package includes, the areas we
            serve, how to book, and what we need from you. If your question
            isn't answered here, call or text{' '}
            <a href="tel:8183819905" className="text-red-500 hover:underline" data-id="element-768" data-mp="158il89p5er9i">
              (818) 381-9905
            </a>
            .
          </p>
        </div>
      </header>

      {/* Business at a glance */}
      <section className="w-full bg-black py-12 px-4" aria-labelledby="business-facts" data-id="element-769" data-mp="1f7870dk60vv1">
        <div className="max-w-4xl mx-auto" data-id="element-770" data-mp="1a7v8uwnug2d5">
          <h2 id="business-facts" className="text-2xl md:text-3xl font-black uppercase text-white mb-6" data-id="element-771" data-mp="1a8wt9rqr217j">
            <span className="text-red-600" data-id="element-772" data-mp="33nq4kucnjb4r">Business</span> at a Glance
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-773" data-mp="38n2rff3iwi7c">
            {BUSINESS_FACTS.map(fact => <div key={fact.label} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5" data-id="element-774" data-mp="3dg48ntr7ctu3">
                <dt className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-775" data-mp="3ie5om5tc0d1c">
                  {fact.label}
                </dt>
                <dd className="text-gray-200 leading-relaxed" data-id="element-776" data-mp="2pprqzr65niqb">{fact.value}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Pricing */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby="faq-pricing" data-id="element-777" data-mp="2ur7f5s3mq5cx">
        <div className="max-w-4xl mx-auto" data-id="element-778" data-mp="2zluup1gnnpfd">
          <h2 id="faq-pricing" className="text-2xl md:text-3xl font-black uppercase text-white mb-2" data-id="element-779" data-mp="9zov847yo7ec">
            <span className="text-red-600" data-id="element-780" data-mp="ewxd03p769f2">Packages</span> &amp; Pricing
          </h2>
          <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-781" data-mp="1zgakpxkjlpti">
            Every package is priced in two tiers: SM/MID for sedans and smaller
            vehicles, and SUV/CROSS for SUVs and crossovers. Prices range from
            $90 to $350.
          </p>

          <div className="space-y-4" data-id="element-782" data-mp="1pnv351d5r395">
            {PRICING_TABLE.map(pkg => <div key={pkg.name} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-6" data-id="element-783" data-mp="1kqbq24s74wis">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3" data-id="element-784" data-mp="1fsthifi3cjbb">
                  <h3 className="text-xl font-black uppercase text-white" data-id="element-785" data-mp="1bpnbn8yw4u5d">
                    {pkg.name}
                  </h3>
                  <p className="text-gray-300 font-bold" data-id="element-786" data-mp="16tg3d9j0v6k9">
                    SM / MID{' '}
                    <span className="text-red-500" data-id="element-787" data-mp="11u3g4no8nxh5">{pkg.smMid}</span>
                    <span className="text-zinc-700 mx-2" data-id="element-788" data-mp="314bnn93kka2v">·</span>
                    SUV / CROSS{' '}
                    <span className="text-red-500" data-id="element-789" data-mp="2w7bdon3lmn6h">{pkg.suvCross}</span>
                  </p>
                </div>
                <p className="text-gray-400 mb-3 leading-relaxed" data-id="element-790" data-mp="ufjfma987tuc">{pkg.note}</p>
                <p className="text-gray-300 leading-relaxed" data-id="element-791" data-mp="z8vzy86vheha">
                  <span className="font-bold text-white" data-id="element-792" data-mp="kiy5jaicnsjn">Includes: </span>
                  {pkg.includes.join(', ')}.
                </p>
              </div>)}
          </div>

          <h3 className="text-lg font-black uppercase text-white mt-8 mb-4" data-id="element-793" data-mp="pfx17xhnw7jy">
            Extra Services
          </h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-794" data-mp="1i6oryffyk3zw">
            {EXTRAS_TABLE.map(extra => <div key={extra.name} className="flex items-center justify-between gap-4 bg-zinc-900/40 border border-zinc-800 rounded-xl px-5 py-4" data-id="element-795" data-mp="1i4cdic9esbrl">
                <dt className="text-gray-200 font-bold" data-id="element-796" data-mp="187hii3ccgti2">{extra.name}</dt>
                <dd className="text-red-500 font-black whitespace-nowrap" data-id="element-797" data-mp="3nz5cbpw2a5wn">
                  {extra.price}
                </dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* FAQ categories */}
      {FAQ_CATEGORIES.map(category => <section key={category.id} id={category.id} className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby={`${category.id}-heading`} data-id="element-798" data-mp="2aza7a432ajwp">
          <div className="max-w-4xl mx-auto" data-id="element-799" data-mp="2k230wdzpv7pp">
            <h2 id={`${category.id}-heading`} className="text-2xl md:text-3xl font-black uppercase text-white mb-3" data-id="element-800" data-mp="2jzp4o1tavpgk">
              {category.title}
            </h2>
            <p className="text-gray-400 mb-6 leading-relaxed max-w-3xl" data-id="element-801" data-mp="1sboneivm7xek">
              {category.intro}
            </p>
            <dl className="space-y-4" data-id="element-802" data-mp="1nddh2i4j4513">
              {category.faqs.map(faq => <div key={faq.question} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-6" data-id="element-803" data-mp="21373ktskad7n">
                  <dt className="text-white font-bold text-lg" data-id="element-804" data-mp="1wb5wdma93coh">
                    {faq.question}
                  </dt>
                  <dd className="mt-3 text-gray-300 text-base leading-relaxed" data-id="element-805" data-mp="18l3538e5uaqx">
                    {faq.answer}
                  </dd>
                </div>)}
            </dl>
          </div>
        </section>)}

      {/* Where to learn more */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" aria-labelledby="learn-more" data-id="element-806" data-mp="1yewtrx0yms8g">
        <div className="max-w-4xl mx-auto" data-id="element-807" data-mp="22g0cqseb14dw">
          <h2 id="learn-more" className="text-2xl md:text-3xl font-black uppercase text-white mb-6" data-id="element-808" data-mp="22eyqrsvr2xhe">
            Learn More About Each <span className="text-red-600" data-id="element-809" data-mp="27d8ks05qns1f">Service</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-id="element-810" data-mp="2ce6d35kgq2gn">
            <FaqLink to="/services/mobile-detailing" label="Mobile Detailing" data-id="element-811" data-mp="2hczfa1uau71i" />
            <FaqLink to="/full-detail" label="Full Detail" data-id="element-812" data-mp="2m4rawqsbb2yi" />
            <FaqLink to="/wash-wax" label="Wash & Wax" data-id="element-813" data-mp="2m5aojvaxj56q" />
            <FaqLink to="/basic-wash" label="Basic Wash" data-id="element-814" data-mp="2q6cibwm2up1b" />
            <FaqLink to="/services/interior-detailing" label="Interior Detailing" data-id="element-815" data-mp="2q7xbpiwno1xl" />
            <FaqLink to="/services/exterior-detailing" label="Exterior Detailing" data-id="element-816" data-mp="2v32wufi8hckm" />
            <FaqLink to="/services/hybrid-ceramic-spray" label="Hybrid Ceramic Spray" data-id="element-817" data-mp="304s6prtpzk74" />
            <FaqLink to="/services/headlight-restoration" label="Headlight Restoration" data-id="element-818" data-mp="3a909otb2tvzq" />
            <FaqLink to="/services/engine-bay-detailing" label="Engine Bay Detailing" data-id="element-819" data-mp="3582rwhtzb3yr" />
          </div>

          <h3 className="text-lg font-black uppercase text-white mb-4" data-id="element-820" data-mp="30asv2h49fq0w">
            Service Areas
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-821" data-mp="2wa0mlq37pp5o">
            {CITY_PAGE_AREAS.map(city => <FaqLink key={city} to={`/service-areas/${city.toLowerCase().replace(/\s+/g, '-')}`} label={city} data-id="element-822" data-mp="3sy6xguqq9dse" />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full bg-black py-12 px-4 border-t border-zinc-900" data-id="element-823" data-mp="3j7u2s9p7848j">
        <div className="max-w-4xl mx-auto text-center" data-id="element-824" data-mp="3f7toqq60hv20">
          <h2 className="text-2xl md:text-3xl font-black uppercase text-white mb-3" data-id="element-825" data-mp="3f57iquyrrnlw">
            Still Have a Question?
          </h2>
          <p className="text-gray-400 mb-6" data-id="element-826" data-mp="atc0yppupkxd">
            Call or text Mike directly and get a straight answer.
          </p>
          <a href="tel:8183819905" className="inline-flex items-center gap-3 px-8 py-4 bg-red-600 text-white font-black uppercase tracking-wide rounded-lg hover:bg-red-500 transition-colors duration-150" aria-label="Call Mike's Pro Detail at (818) 381-9905" data-id="element-827" data-mp="lhoipl31jcdh">
            <Phone className="w-5 h-5" aria-hidden="true" data-id="element-828" data-mp="pi8dwj4205wz" />
            (818) 381-9905
          </a>
        </div>
      </section>
    </main>;
}
function FaqLink({
  to,
  label
}: {
  to: string;
  label: string;
}) {
  return <Link to={to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-colors duration-150" data-id="element-829" data-mp="zgxzomz2maii">
      {label} &rarr;
    </Link>;
}